/**
 * Per-state transition queries (By Source, Referral, Landing URL, All Practices,
 * legacy Analytics -> Reports).
 *
 * A `state_transition_params` WITHOUT a `state` makes the backend count every
 * state log of any lead that had one in the window, so grouping by state
 * over-counts ("every stage ever": Booked 56 vs the true 42). The fix is one
 * call per state with that state pinned, reading the count for that state.
 */
import { callApi } from 'src/utils/apiCall';
import { toPracticeIds, practiceInSuffix } from 'src/utils/practiceInSuffix';

export const REPORT_STATES = ['CONSULTATION_BOOKED', 'CONSULTATION_ATTENDED', 'TREATMENT_STARTED', 'CLOSED', 'FAILED_TO_ATTEND_CONSULTATION'];
// BE contract (reporting-phase1): restrict the lead_treatments join to the treatment(s)
// that carry the in-window transition, so revenue / category grouping count only those.
export const SUM_SCOPE = 'transitioned';
export const STATE_LOG_KEY = 'lead_treatments__state_transition_logs__state';

export const buildStateParams = (state, start, end) =>
    encodeURIComponent(JSON.stringify({ state, timestamp_gte: start, timestamp_lte: end }));

export const buildPerStateUrl = ({ baseUrl, groupByFields = [], state, start, end, suffix = '', withRevenue = true }) => {
    // No state-log group_by: it joins ALL state logs of a treatment and multiplies Sum(custom_price)
    // when a treatment has 2 logs of the same state. The state + window are pinned in
    // state_transition_params and sum_scope=transitioned, so every returned row already belongs to `state`.
    const groups = groupByFields.map((f) => `group_by[${f}]=`).join('&');
    const revenue = withRevenue ? '&aggregate[Sum]=lead_treatments__custom_price' : '';
    return `${baseUrl}/leads/?${groups ? `${groups}&` : ''}aggregate[Count]=id${revenue}&sum_scope=${SUM_SCOPE}&state_transition_params=${buildStateParams(state, start, end)}${suffix}`;
};

// Returns { [state]: rows[] }; every row belongs to that state (pinned in the request).
export const fetchPerStateGroupedRows = async ({ baseUrl, groupByFields = [], start, end, suffix = '', states = REPORT_STATES, withRevenue = true }) => {
    const results = await Promise.all(states.map(async (state) => {
        const res = await callApi(buildPerStateUrl({ baseUrl, groupByFields, state, start, end, suffix, withRevenue }));
        if (res && res.ok === false) throw new Error(`Report request failed for ${state}`);
        const body = res?.data?.data;
        // Ungrouped calls return a single aggregate object rather than an array.
        let rows = [];
        if (Array.isArray(body)) rows = body;
        else if (body && typeof body === 'object') rows = [body];
        return [state, rows];
    }));
    return Object.fromEntries(results);
};

// Pure fold: enquiry rows (created_at) + per-state rows -> table rows.
// Revenue = custom_price summed over the CLOSED call only (completed revenue).
export const foldGroupedStatusRows = ({ enquiryRows, perState, groupOf, idKey }) => {
    const groupMap = {};
    const ensureGroup = (group) => {
        if (!groupMap[group]) {
            groupMap[group] = {
                id: group,
                [idKey]: group,
                enquiries: 0,
                CONSULTATION_BOOKED: 0,
                CONSULTATION_ATTENDED: 0,
                TREATMENT_STARTED: 0,
                FAILED_TO_ATTEND_CONSULTATION: 0,
                CLOSED: 0,
                revenue: 0,
            };
        }
        return groupMap[group];
    };
    enquiryRows.forEach((item) => {
        const group = groupOf(item);
        if (!group) return;
        ensureGroup(group).enquiries = item.count_id || 0;
    });
    Object.entries(perState || {}).forEach(([state, rows]) => {
        rows.forEach((item) => {
            const group = groupOf(item);
            if (!group) return;
            const bucket = ensureGroup(group);
            if (Object.prototype.hasOwnProperty.call(bucket, state) && state !== 'revenue') {
                bucket[state] += item.count_id || 0;
            }
            if (state === 'CLOSED') bucket.revenue += item.sum_lead_treatments__custom_price || 0;
        });
    });
    const rows = Object.values(groupMap).map((row) => ({
        ...row,
        conversionRate: row.enquiries > 0 ? ((row.TREATMENT_STARTED / row.enquiries) * 100).toFixed(1) : 0,
    }));
    rows.sort((a, b) => b.enquiries - a.enquiries);
    return rows;
};

// Period totals on the same definitions as the table (previous-period KPIs):
// leads created, completed revenue (CLOSED transitions), treatments started.
export const fetchPeriodStateTotals = async (practiceIdOrIds, { start, end }) => {
    const ids = toPracticeIds(practiceIdOrIds);
    const baseUrl = `/api/v1/practices/${ids[0]}/reports`;
    const suffix = practiceInSuffix(ids);
    const [leadsRes, perState] = await Promise.all([
        callApi(`${baseUrl}/leads/?aggregate[Count]=id&created_at__gte=${start}&created_at__lte=${end}${suffix}`),
        fetchPerStateGroupedRows({ baseUrl, start, end, suffix, states: ['TREATMENT_STARTED', 'CLOSED'] }),
    ]);
    const sum = (rows, key) => rows.reduce((a, r) => a + (r[key] || 0), 0);
    return {
        leads: leadsRes?.data?.data?.count_id || 0,
        started: sum(perState.TREATMENT_STARTED, 'count_id'),
        revenue: sum(perState.CLOSED, 'sum_lead_treatments__custom_price'),
    };
};

